/* eslint-disable camelcase */
import { User } from 'src/services/user/user.types';
import { invisiblyApi } from '../../../redux/api/invisibly-api';
import { InvisiblyApiResponse } from '../../../services/api/api-response.types';
import {
  Account,
  Campaign,
  GetAccountsParams,
  GetCampaignsParams,
  ManagedPaginatedResult,
  UpdateAccountParams,
  UpdateCampaignParams,
} from '../types/client-management.types';
import { buildManageQueryParams } from '../utils/manage-query-params';

const defaultManagePage = 1;
const defaultManagePageSize = 25;
/** GET manage lists: API max page_size */
const ACCOUNTS_MAX_PAGE_SIZE = 100;
const CAMPAIGNS_MAX_PAGE_SIZE = 100;

/** When neither `page` nor `page_size` is set, omit both so the API returns an unpaginated list. */
function wantsManagePagination(arg: { page?: number; page_size?: number } | void): boolean {
  if (arg == null) return false;
  return typeof arg.page === 'number' || typeof arg.page_size === 'number';
}

/**
 * GET /b2b/manage/accounts — only: search, attention_needed, page, page_size
 * (see AccountInternal API contract)
 */
function mergeAccountsQueryParams(arg: GetAccountsParams | void): Record<string, unknown> {
  const a = arg ?? {};
  const out: Record<string, unknown> = {};

  if (wantsManagePagination(a)) {
    const page = a.page ?? defaultManagePage;
    let page_size = a.page_size ?? defaultManagePageSize;
    if (page_size > ACCOUNTS_MAX_PAGE_SIZE) {
      page_size = ACCOUNTS_MAX_PAGE_SIZE;
    }
    if (page_size < 1) {
      page_size = 1;
    }
    out.page = page;
    out.page_size = page_size;
  }

  const search = a.search !== undefined && a.search !== null ? String(a.search).trim() : '';
  if (search !== '') {
    out.search = search;
  }
  if (a.attention_needed === true || a.attention_needed === false) {
    out.attention_needed = a.attention_needed;
  }
  return out;
}

/**
 * GET /b2b/manage/campaigns — only: search, attention_needed, page, page_size
 * (see CampaignInternal API contract)
 */
function mergeCampaignsQueryParams(arg: GetCampaignsParams | void): Record<string, unknown> {
  const a = arg ?? {};
  const out: Record<string, unknown> = {};

  if (wantsManagePagination(a)) {
    const page = a.page ?? defaultManagePage;
    let page_size = a.page_size ?? defaultManagePageSize;
    if (page_size > CAMPAIGNS_MAX_PAGE_SIZE) {
      page_size = CAMPAIGNS_MAX_PAGE_SIZE;
    }
    if (page_size < 1) {
      page_size = 1;
    }
    out.page = page;
    out.page_size = page_size;
  }

  const search = a.search !== undefined && a.search !== null ? String(a.search).trim() : '';
  if (search !== '') {
    out.search = search;
  }
  if (a.attention_needed === true || a.attention_needed === false) {
    out.attention_needed = a.attention_needed;
  }
  return out;
}

const accountsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getAccounts: builder.query<ManagedPaginatedResult<Account>, GetAccountsParams | void>({
      query: (arg) => ({
        method: 'GET',
        url: 'b2b/manage/accounts',
        params: buildManageQueryParams(mergeAccountsQueryParams(arg)),
      }),
      transformResponse(response: InvisiblyApiResponse<unknown>): ManagedPaginatedResult<Account> {
        const res = response as unknown as Record<string, unknown>;
        const item = (res.item ?? {}) as Record<string, unknown>;
        const items = (item.items as Account[]) ?? [];
        return {
          items,
          total_objects: Number(item.total_objects ?? 0),
          total_pages: Number(item.total_pages ?? 0),
          page: Number(item.page ?? defaultManagePage),
          page_size: Number(item.page_size ?? defaultManagePageSize),
        };
      },
    }),
    updateAccount: builder.mutation<Account, UpdateAccountParams>({
      query: ({ accountId, ...updateData }) => ({
        method: 'PATCH',
        url: `b2b/manage/accounts/${accountId}`,
        data: updateData,
      }),
      transformResponse(response: InvisiblyApiResponse<Account>) {
        return response.item;
      },
      // invalidatesTags: ['Account'],
    }),
    getCampaigns: builder.query<ManagedPaginatedResult<Campaign>, GetCampaignsParams | void>({
      query: (arg) => ({
        method: 'GET',
        url: 'b2b/manage/campaigns',
        params: buildManageQueryParams(mergeCampaignsQueryParams(arg)),
      }),
      transformResponse(response: InvisiblyApiResponse<unknown>): ManagedPaginatedResult<Campaign> {
        const res = response as unknown as Record<string, unknown>;
        const item = (res.item ?? {}) as Record<string, unknown>;
        const items = (item.items as Campaign[]) ?? [];
        return {
          items,
          total_objects: Number(item.total_objects ?? 0),
          total_pages: Number(item.total_pages ?? 0),
          page: Number(item.page ?? defaultManagePage),
          page_size: Number(item.page_size ?? defaultManagePageSize),
        };
      },
    }),
    updateCampaign: builder.mutation<Campaign, UpdateCampaignParams>({
      query: ({ campaignId, ...updateData }) => ({
        method: 'PATCH',
        url: `b2b/manage/campaigns/${campaignId}`,
        data: updateData,
      }),
      transformResponse(response: InvisiblyApiResponse<Campaign>) {
        return response.item;
      },
      // invalidatesTags: ['Campaign'],
    }),
    getInternalUsers: builder.query<User[], void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/manage/users',
      }),
      transformResponse(response: InvisiblyApiResponse<User[]>) {
        return response.item;
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetAccountsQuery,
  useUpdateAccountMutation,
  useGetCampaignsQuery,
  useUpdateCampaignMutation,
  useGetInternalUsersQuery,
} = accountsEndpoints;
